import { useEffect, useState, useSyncExternalStore } from 'react';
import { subscribeEvents, isLeader, subscribeLeadership, phoneState } from '../lib/telephony';
import type { TelephonyEvent } from '../lib/telephony';

export interface LiveCallStats {
  packetLossPct: number;
  jitterMs: number;
  rttMs: number;
  quality: 'good' | 'fair' | 'poor';
  lastUpdated: Date;
}

/**
 * Streams live quality samples for the given clientCallId.
 * Returns null until the first `quality_sample` event arrives.
 */
export function useCallStats(clientCallId: string | null): LiveCallStats | null {
  const [stats, setStats] = useState<LiveCallStats | null>(null);

  useEffect(() => {
    if (!clientCallId) {
      setStats(null);
      return;
    }

    return subscribeEvents((e: TelephonyEvent) => {
      if (e.type !== 'quality_sample') return;
      if (e.clientCallId !== clientCallId) return;

      const sample = (e.metadata as { sample?: Record<string, unknown> } | undefined)?.sample;
      if (!sample) return;

      const received = Number(sample.packetsReceived ?? 0);
      const lost = Number(sample.packetsLost ?? 0);
      const denom = received + lost;
      const lossPct = denom > 0 ? (lost / denom) * 100 : 0;
      const quality: LiveCallStats['quality'] =
        lossPct < 2 ? 'good' : lossPct < 5 ? 'fair' : 'poor';

      setStats({
        packetLossPct: lossPct,
        jitterMs: Number(sample.jitterMs ?? 0),
        rttMs: Number(sample.rttMs ?? 0),
        quality,
        lastUpdated: new Date(),
      });
    });
  }, [clientCallId]);

  return stats;
}

function mirrorToLive(s: ReturnType<typeof phoneState.getStats>): LiveCallStats | null {
  return s ? { ...s, lastUpdated: new Date() } : null;
}

/**
 * Quality of the active call regardless of tab role: the owner samples it live
 * (useCallStats), followers read the mirrored value broadcast in the snapshot.
 */
export function useMirroredCallStats(clientCallId: string | null): LiveCallStats | null {
  const live = useCallStats(clientCallId);
  const [mirror, setMirror] = useState<LiveCallStats | null>(() => mirrorToLive(phoneState.getStats()));
  // Reactief: een tab kan mid-sessie owner worden (vorige owner-tab dicht);
  // een niet-reactieve isLeader()-read bleef dan de stale mirror tonen.
  const leader = useSyncExternalStore(subscribeLeadership, isLeader);

  useEffect(() => {
    setMirror(mirrorToLive(phoneState.getStats()));
    return phoneState.subscribe(() => setMirror(mirrorToLive(phoneState.getStats())));
  }, []);

  return leader ? live : mirror;
}
